import { JSONSchema7 } from 'json-schema';
import { array, string, object, boolean, number, ref } from 'yup';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import formatName from '../formatName';
import checkAllRulesHaveComponents from '../Shared/RulesEngine/checkAllRulesHaveComponents';
import checkInBetweenConditionalRulesAreValid from '../Shared/RulesEngine/checkInBetweenConditionalRulesAreValid';
import getYupSchemaForRulesEngine from '../Shared/RulesEngine/getYupSchemaForRulesEngine';

export default (properties: JSONSchema7Properties) => {
  const rulesSchema = properties['rules'] as JSONSchema7;
  const rulesMinQty = (rulesSchema?.minItems as number) || 1;
  const rulesMaxQty = (rulesSchema?.maxItems as number) || 10;

  return object({
    [`${formatName(properties['label']?.$id as string)}`]: string()
      .required('Required')
      .max(250, 'Must be at most 250 characters'),
    [`${formatName(properties['id']?.$id as string)}`]: string()
      .required('Required')
      .matches(/^[a-z0-9]+$/i, 'Must be alphanumeric'),
    [`${formatName(properties['required']?.$id as string)}`]: boolean(),
    [`${formatName(properties['minimum']?.$id as string)}`]: number()
      .transform((currentValue, originalValue) => {
        return originalValue === '' ? 0 : currentValue;
      })
      .typeError('Must be a number')
      .required('Required')
      .test(
        'greaterOrEqualToZero',
        'Must be a number greater than zero',
        (number) => /^[0-9][0-9]*$/.test(String(number))
      )
      .lessThan(
        ref(`${formatName(properties['maximum']?.$id as string)}`),
        'Must be less than maximum value'
      ),
    [`${formatName(properties['maximum']?.$id as string)}`]: number()
      .transform((currentValue, originalValue) => {
        return originalValue === '' ? 0 : currentValue;
      })
      .typeError('Must be a number')
      .required('Required')
      .test(
        'greaterOrEqualToZero',
        'Must be a number greater than zero',
        (number) => /^[0-9][0-9]*$/.test(String(number))
      )
      .moreThan(
        ref(`${formatName(properties['minimum']?.$id as string)}`),
        'Must be greater than minimum value'
      ),
    [`${formatName(properties['decimalPlaces']?.$id as string)}`]: number()
      .transform((currentValue, originalValue) => {
        return originalValue === '' ? 0 : currentValue;
      })
      .typeError('Must be a number')
      .required('Required')
      .test(
        'greaterOrEqualToZero',
        'Must be a number greater than zero',
        (number) => /^[0-9][0-9]*$/.test(String(number))
      ),
    [`${formatName(properties['utilizeHiddenComponents']?.$id as string)}`]:
      boolean(),
    [`${formatName(properties['rules']?.$id as string)}`]: array()
      .of(getYupSchemaForRulesEngine())
      .when('utilizeHiddenComponents', {
        is: true,
        then: (schema) =>
          schema
            .min(rulesMinQty, `Must add at least ${rulesMinQty} rule`)
            .test(
              'allRulesMustHaveComponents',
              'All rules must have component(s) assigned',
              function () {
                return checkAllRulesHaveComponents(this.parent['rules']);
              }
            )
            .test(
              'allInBetweenRulesMustHaveValidNumbers',
              'All rules with a range of values must have a minimum value lower than the maximum value',
              function () {
                return checkInBetweenConditionalRulesAreValid(
                  this.parent['rules']
                );
              }
            ),
        otherwise: (schema) => schema.min(0)
      })
      .max(rulesMaxQty, `Must have a maximum of ${rulesMaxQty} rules`),
    [`${formatName(properties['gridSize']?.$id as string)}`]: number()
  });
};
